import { styled } from "goober";
import { useMemo, useState } from "react";
import type { Execution as ExecutionType } from "../../shared/schema";
import { executionContentPath } from "../lib/apiPaths";
import { Button } from "./Button";
import type { CodeSelection } from "./CodeEditor";
import { EmptyState } from "./EmptyState";
import {
  FileRender,
  type FileRenderControl,
  type FileSelection,
} from "./FileRender";
import { type IconName } from "./Icon";
import { WavyLine } from "./WavyLine";
import type { CodeSnippet } from "./Chat/chatTypes";

const animalIcons: IconName[] = ["rabbit", "squirrel", "snail", "bird", "rat"];

const LogsButton = styled(Button)`
  &&& {
    background: var(--alpha4);
  }
  &&&:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

type Props = {
  execution: Pick<ExecutionType, "id" | "status" | "kind">;
  field?: "input" | "output";
  stepName?: string;
  edgeRoute?: string;
  codeSnippets?: readonly CodeSnippet[];
  onShowLogs?: () => void;
  onControls?: (controls: FileRenderControl[]) => void;
  onSelectionChange?: (selections: CodeSelection[]) => void;
};

export function ExecutionContent({
  execution,
  field = "output",
  stepName,
  edgeRoute,
  codeSnippets,
  onShowLogs,
  onControls,
  onSelectionChange,
}: Props) {
  const [animalIcon] = useState(
    () =>
      animalIcons[Math.floor(Math.random() * animalIcons.length)] ?? "rabbit"
  );

  const fileId = `${stepName}/output`;
  const selectedRanges = useMemo(
    () =>
      codeSnippets
        ?.filter((s) => s.filePath === fileId)
        .map((s) => ({ from: s.from, to: s.to, color: s.color })),
    [codeSnippets, fileId]
  );

  const handleSelect = onSelectionChange
    ? (selections: FileSelection[]) =>
        onSelectionChange(
          selections.map((s) => ({
            id: `output:${s.from}-${s.to}`,
            filePath: "output",
            from: s.from,
            to: s.to,
            fromLine: s.fromLine,
            toLine: s.toLine,
            fromChar: 0,
            toChar: 0,
            text: s.text,
          }))
        )
    : undefined;

  if (execution.status === "error") {
    return (
      <EmptyState
        iconName="heart-crack"
        iconSize={24}
        maxWidth={220}
        title="Step failed"
        description="Something went wrong while running this step."
        action={
          onShowLogs && (
            <LogsButton variant="secondary" onClick={onShowLogs}>
              View logs
            </LogsButton>
          )
        }
      />
    );
  }

  if (execution.kind === "build") {
    return execution.status === "pending" ? (
      <EmptyState
        icon={<WavyLine />}
        maxWidth={220}
        title="Building"
        description="Installing dependencies…"
      />
    ) : (
      <EmptyState
        iconName="package-2"
        iconSize={24}
        maxWidth={220}
        title="Built successfully"
        description="Dependencies are installed and up to date."
      />
    );
  }

  if (execution.status === "pending") {
    return (
      <EmptyState
        icon={<WavyLine />}
        maxWidth={220}
        title="Running"
        description="Hang tight, output will appear when this step finishes."
      />
    );
  }

  return (
    <FileRender
      url={executionContentPath(execution.id, field)}
      bleed={12}
      baseRoute={edgeRoute}
      selectedRanges={selectedRanges}
      onSelect={handleSelect}
      onControls={onControls}
      emptyState={
        <EmptyState
          iconName={animalIcon}
          iconSize={24}
          maxWidth={220}
          title={field === "input" ? "No input" : "No output"}
          description={
            field === "input"
              ? "This step wasn't triggered with any input."
              : "This step didn't return any output."
          }
        />
      }
    />
  );
}
